import React, { useMemo, useState } from 'react';
import L from 'leaflet';
import { Marker, Popup, Tooltip } from 'react-leaflet';

import { formatValue } from '@/entities/dashboard';
import { MAP_REPORTS, MAP_REPORT_CONFIG, type MapReport } from '@/features/map-layers';
import type { MapCluster } from '@/features/map-grouping';

import { buildPieSlices, pieRadius, type PieSlice } from '../lib/pie-geometry';
import { ClusterPopup } from '../../../ui/cluster-popup';
import type { MapDataFilters } from '../../../model/map-provider.interface';

const sliceSvg = (slice: PieSlice, radius: number) =>
  slice.path === null
    ? `<circle cx="${radius}" cy="${radius}" r="${radius}" fill="${slice.color}" fill-opacity="0.5" data-report="${slice.report}"/>`
    : `<path d="${slice.path}" fill="${slice.color}" fill-opacity="0.5" data-report="${slice.report}"/>`;

const createPieIcon = (slices: PieSlice[], radius: number) => {
  const size = radius * 2;

  return L.divIcon({
    className: 'map-cluster-pie',
    html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${slices.map((slice) => sliceSvg(slice, radius)).join('')}</svg>`,
    iconSize: [size, size],
    iconAnchor: [radius, radius]
  });
};

/** Which wedge was clicked — every wedge carries its report in a `data-report` attribute. */
const readSliceReport = (target: EventTarget | null): MapReport | null => {
  const report = target instanceof Element ? target.getAttribute('data-report') : null;

  return report && (MAP_REPORTS as readonly string[]).includes(report) ? (report as MapReport) : null;
};

interface ClusterPieLayerProps {
  cluster: MapCluster;
  /** Largest cluster on the map — marker radius is relative to it, as in legacy. */
  maxTotal: number;
  weeksAgo: number;
  dimensionLabel?: string;
  /** Must be a stable reference — this component is memoised on it. */
  dataFilters?: MapDataFilters;
}

export const ClusterPieLayer = React.memo(function ClusterPieLayer({
  cluster,
  maxTotal,
  weeksAgo,
  dimensionLabel,
  dataFilters
}: ClusterPieLayerProps) {
  const radius = pieRadius(cluster.total, maxTotal);
  const slices = useMemo(() => buildPieSlices(cluster.values, radius), [cluster.values, radius]);
  const icon = useMemo(() => createPieIcon(slices, radius), [slices, radius]);

  const [activeReport, setActiveReport] = useState<MapReport | null>(null);
  const report = activeReport ?? slices[0]?.report;

  if (!report) return null;

  return (
    <Marker
      position={[cluster.lat, cluster.lng]}
      icon={icon}
      eventHandlers={{
        click: (event) => setActiveReport(readSliceReport(event.originalEvent.target))
      }}>
      <Tooltip direction="top">
        <span className="font-semibold">{dimensionLabel ? `${dimensionLabel}: ${cluster.name}` : cluster.name}</span>
        {slices.map((slice) => (
          <div key={slice.report} className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: slice.color }} />
            {formatValue(cluster.values[slice.report], 'W')}
          </div>
        ))}
      </Tooltip>

      <Popup maxWidth={400} minWidth={300}>
        <ClusterPopup
          cluster={cluster}
          report={report}
          weeksAgo={weeksAgo}
          dimensionLabel={dimensionLabel}
          dataFilters={dataFilters}
          color={MAP_REPORT_CONFIG[report].color}
        />
      </Popup>
    </Marker>
  );
});
